import React from 'react';
import { Flex } from 'antd';
import { faFrown, faLaugh, faMeh, faSmile, faTired } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExToggleButton } from 'jf/components/DevExToggleButton';
import { DEFAULT_LIKERT_LABELS } from 'jf/pages/studyBuilder/customizeStudy/CustomizablePrompt';
import { useLivePropState } from 'jf/utils/useLivePropState';

const LIKERT_SCORES = [1, 2, 3, 4, 5];
export const LIKERT_ICONS = [faTired, faFrown, faMeh, faSmile, faLaugh];
const LIKERT_LABELS = ['Strongly disagree', '', 'Neutral', '', 'Strongly agree'];

interface LikertScaleProps {
  value?: number;
  onChange?: (score: number) => void;
  disabled?: boolean;
  numeric?: boolean;
  polarity: number;
  choiceLabels?: string[];
  editing?: boolean; // tells the component if it's in edit mode or not
  type?: 'matrix' | 'default'; // 'matrix' for matrix prompts, 'default' for regular Likert scales
}

export const LikertScaleLabels = (
  props: Pick<LikertScaleProps, 'numeric' | 'editing' | 'choiceLabels'> & {
    style?: React.CSSProperties;
  }
) => {
  const theme = useDevExTheme();
  const spacing = theme.variable.spacing.sm;
  return (
    <>
      {props.numeric ? (
        <>
          {/* if we're on the edit phase, we have inputs in the parent component that represent the 
          labels. this component is used for both the editing state and the rendered state, so we hide 
          these labels in favor of the inputs in the parent */}
          {!props.editing && (
            <Flex
              justify="space-between"
              align="flex-end"
              style={{
                fontSize: theme.variable.fontSize.sm,
                color: theme.color.text.secondary,
                ...props.style,
              }}
            >
              {props.choiceLabels && props.choiceLabels.length > 1 ? (
                <>
                  {props.choiceLabels &&
                    props.choiceLabels.map((label, index) => {
                      const count = props.choiceLabels?.length || 0;
                      const width = `${100 / count}%`;
                      let align: 'center' | 'left' | 'right' = 'center'; // typed specifically to avoid lint complaints
                      if (index === 0) {
                        align = 'left';
                      } else if (index === count - 1) {
                        align = 'right';
                      }
                      return (
                        <span
                          style={{
                            width: width,
                            textAlign: align,
                          }}
                          key={label}
                        >
                          {label}
                        </span>
                      );
                    })}
                </>
              ) : (
                <>
                  <span>{DEFAULT_LIKERT_LABELS[0]}</span>
                  <span>{DEFAULT_LIKERT_LABELS[1]}</span>
                </>
              )}
            </Flex>
          )}
        </>
      ) : (
        <Flex
          justify="space-between"
          style={{
            fontSize: theme.variable.fontSize.sm,
            color: theme.color.text.secondary,
            gap: spacing,
            ...props.style,
          }}
        >
          {LIKERT_LABELS &&
            LIKERT_LABELS.map((label, index) => {
              const count = LIKERT_LABELS?.length || 0;
              const width = `${100 / count}%`;
              let align: 'center' | 'left' | 'right' = 'center'; // typed specifically to avoid lint complaints
              if (index === 0) {
                align = 'left';
              } else if (index === count - 1) {
                align = 'right';
              }
              return (
                <span
                  style={{
                    width: `calc(${width} - ${parseInt(spacing)}px)`,
                    textAlign: align,
                    textWrap: 'nowrap',
                  }}
                  key={label}
                >
                  {label}
                </span>
              );
            })}
        </Flex>
      )}
    </>
  );
};

export const LikertScale: React.FC<LikertScaleProps> = (props) => {
  const theme = useDevExTheme();

  const [selectedScore, setSelectedScore] = useLivePropState(props.value, props.onChange);

  const negativePolarity = props.polarity < 0;

  const polarizeIndex = (index: number) => (negativePolarity ? 4 - index : index);

  return (
    <Flex
      vertical
      align="stretch"
      gap={theme.variable.spacing.sm}
      flex={props.type === 'matrix' ? 1 : undefined}
    >
      <Flex
        gap={props.type === 'matrix' ? theme.variable.spacing.md : '2.5%'}
        style={{ height: props.type === 'matrix' ? 'min(48px, 10vw)' : 'min(72px, 14vw)' }}
      >
        {LIKERT_LABELS.map((label, i) => {
          const score = LIKERT_SCORES[polarizeIndex(i)];
          const icon = LIKERT_ICONS[polarizeIndex(i)];

          return (
            <DevExToggleButton
              key={score}
              toggled={score === selectedScore}
              onClick={() => setSelectedScore(score)}
              disabled={props.disabled}
              style={{ opacity: props.disabled ? theme.variable.opacity.disabled : 1 }}
            >
              {props.numeric ? <>{score}</> : <FontAwesomeIcon icon={icon} />}
            </DevExToggleButton>
          );
        })}
      </Flex>
      {props.type !== 'matrix' && (
        <LikertScaleLabels
          numeric={props.numeric}
          editing={props.editing}
          choiceLabels={props.choiceLabels}
        />
      )}
    </Flex>
  );
};
